/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { TriangleAlert, X } from 'lucide-react';

/**
 * The one place a failure is allowed to say something.
 *
 * **Two tests hold this from opposite sides**, which is the whole point of it:
 * `circles-view` asserts the banner *speaks* after a failed circle operation, and
 * `pin-todo-view` asserts it *stays empty* through a successful run. Together
 * they are the app's only check that errors are neither swallowed nor invented.
 * The first React draft had no banner at all, so every failed mutation was
 * silent — the board simply rolled back and left the person guessing.
 *
 * It reports and then gets out of the way. Five seconds, then empty: an error
 * that stays on screen becomes furniture, and the person has already lost the
 * one moment where it explained what just happened.
 *
 * A 401 never reaches here. That is a session that ended, not a failure to
 * report, and `<RequireSession>` is already routing to /login — a banner saying
 * "Not authenticated" on the way out is noise on top of a redirect.
 */
const HOLD_MS = 5000;

type Listener = (message: string) => void;
const listeners = new Set<Listener>();

/** Show a message. Called from the mutation cache and from anywhere that fails. */
export function showBanner(message: string): void {
  for (const l of listeners) l(message);
}

/**
 * Anything that throws where nothing was there to catch it.
 *
 * **Both halves matter and they are different events.** `error` is a synchronous
 * throw that reached the window; `unhandledrejection` is a promise nobody
 * handled, which is the shape most failures take in an app built on fetch and a
 * query client. Listening for only the first is why "nothing happened when I
 * clicked" is the commonest bug report a web app gets.
 *
 * The console still gets the original — this does not swallow anything, it adds
 * a person-facing report to a machine-facing one. And it stays deliberately
 * short: a stack trace in a toast is unreadable, and the stack is already one
 * keypress away in the developer tools.
 *
 * Installed once, from the component's own effect, so it is torn down with it
 * and never doubled up under React's strict-mode double mount.
 */
/**
 * A view transition the browser gave up on — which is not a failure of the app.
 *
 * Switching view animates, and on a phone the viewport can change underneath it (the
 * address bar collapsing is enough). The browser then abandons the animation and
 * rejects with `Transition was aborted because of invalid state. Viewport size
 * changed`. Nothing is wrong: the new view is already on screen, only the animation
 * is lost. Reporting it put a red toast on a working page.
 *
 * Matched on the message as well as the name, because `InvalidStateError` is a
 * general error and quieting all of it would hide real ones.
 */
export function isViewTransitionNoise(reason: unknown): boolean {
  if (!reason || typeof reason !== 'object') return false;
  const { name, message } = reason as { name?: unknown; message?: unknown };
  return (
    (name === 'InvalidStateError' || name === 'AbortError')
    && typeof message === 'string'
    && /^Transition was (aborted|skipped)/.test(message)
  );
}

function installGlobalReporting(): () => void {
  const onError = (e: ErrorEvent) => {
    showBanner(short(e.message) || 'Something went wrong.');
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    if (isViewTransitionNoise(reason)) return;
    const text = reason instanceof Error ? reason.message : String(reason ?? '');
    showBanner(short(text) || 'Something went wrong.');
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/**
 * One line, and never a wall of text.
 *
 * Runtime error messages arrive with stacks attached and can be paragraphs long.
 * A toast that grows to cover the board is worse than no toast, so this keeps the
 * first line and caps it — the full text is in the console, which is where
 * anybody investigating will be anyway.
 */
function short(text: string): string {
  const line = (String(text).split('\n')[0] ?? '').trim();
  return line.length > 140 ? `${line.slice(0, 137)}…` : line;
}

export function StatusBanner() {
  const [message, setMessage] = useState('');

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const listener: Listener = (next) => {
      setMessage(next);
      // Restarted on every message, not stacked: two failures in quick
      // succession should leave the second one visible for its full five
      // seconds, not be cut short by the first one's timer.
      clearTimeout(timer);
      timer = setTimeout(() => setMessage(''), HOLD_MS);
    };
    listeners.add(listener);
    const uninstall = installGlobalReporting();
    return () => {
      listeners.delete(listener);
      uninstall();
      clearTimeout(timer);
    };
  }, []);

  // Always in the DOM, empty when there is nothing to say. `pin-todo-view` reads
  // its text and expects '', so it must be present and quiet rather than absent.
  //
  // **Everything added here is textless on purpose.** The icon is an `<svg>` and
  // the dismiss carries only an `aria-label`, so `innerText` is still exactly the
  // message — or exactly `''`. A toast that quietly put the word "Close" into
  // that assertion would have turned a passing test into a lying one.
  return (
    <div
      id="status-banner"
      className={cn(
        'status-banner fixed bottom-5 left-1/2 z-[60] flex max-w-[min(560px,calc(100vw-2rem))] items-center gap-3 rounded-xl bg-ink py-2.5 pl-3.5 pr-2 text-sm font-semibold text-bg shadow-overlay',
        // Slides as well as fades, and only on the way in. Something arriving
        // from off-screen reads as an event; something fading in place reads as
        // a thing that was always there and you had not noticed.
        'transition-[opacity,transform] duration-base ease-out-soft',
        message ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
      )}
      role="status"
      aria-live="polite"
      // `visibility` rather than unmounting, so the element the tests read is
      // always there — and `pointer-events` with it, or an invisible bar would
      // still swallow clicks aimed at the board behind it.
      style={{ visibility: message ? 'visible' : 'hidden', pointerEvents: message ? 'auto' : 'none' }}
    >
      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-danger/25 text-bg" aria-hidden>
        <TriangleAlert className="size-3.5" aria-hidden />
      </span>

      {message}

      {/* Five seconds is right for a message you happened to be looking at and
          wrong for one that arrived while you were reading a card. This is the
          way out that does not involve waiting. */}
      <button
        type="button"
        onClick={() => setMessage('')}
        aria-label="Dismiss"
        className="tap ml-auto grid size-6 shrink-0 place-items-center rounded-lg text-bg/60 transition-colors duration-fast ease-out-soft hover:bg-white/10 hover:text-bg"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}
